<script setup lang="ts">
import {
  appIsAvailable,
  availablePlatformLabels,
  type PlatformFlags,
} from '~/utils/apostatudo/platform-gate';
import { getAssetsUrlForCDN } from '@/utils/template';
import { useAppDownloadStore } from '@/stores/app-download';

/*
 * A parede de plataforma (ENG-824). Irmã direta do `MinigameTurnoverGate`: mesma vaga
 * (véu sobre a folha + painel por cima), mesmas props de composição (`bare`, `unlocks`).
 *
 * A COPY É DERIVADA DAS FLAGS, nunca uma frase fixa por feature. Ela NOMEIA onde dá para
 * usar, e é isso que a separa de uma tela vazia ou de um erro seco: ligar o desktop de
 * volta no backoffice muda a frase sozinho, sem deploy.
 *
 * O CTA só aparece quando o app está entre as liberadas. No caso espelhado — a feature
 * liberada só no navegador, e o jogador dentro do app — um botão de baixar mandaria para
 * o lugar de onde ele já veio.
 *
 * TRÊS APRESENTAÇÕES, UMA COPY. `bare` para quem já tem véu por fora (a roletinha e o
 * check-in, por cima do palco), a caixa padrão para dentro de modal (o promocode), e
 * `panel` para quem OCUPA a folha inteira no lugar do conteúdo (a Loja). A escolha é de
 * vaga, não de mensagem: o título que nomeia as plataformas é o mesmo nos três, porque é
 * ele que cumpre a spec.
 */
const props = withDefaults(
  defineProps<{
    /** As três flags JÁ RESOLVIDAS pelo core. Ausentes = liberado, e a parede não monta. */
    flags: PlatformFlags | null;
    /** O que está bloqueado, no sujeito da frase ("A roleta", "O check-in", "A Loja"). */
    unlocks?: string;
    /**
     * `true` remove a caixa própria (borda, fundo, sombra) — para quando quem desenha o
     * fundo é um VÉU por fora. Duas camadas empilhadas escureceriam o dobro.
     */
    bare?: boolean;
    /**
     * `true` desenha o PAINEL que substitui o conteúdo de uma página — o idioma do
     * `BaseLevelGateNotice`, que é o aviso de nível da própria Loja: brilho radial,
     * emblema, título maior e CTA em gradiente. Sem isto a Loja ficava com uma caixinha
     * de modal perdida no meio de uma folha vazia.
     */
    panel?: boolean;
  }>(),
  { unlocks: 'Esta função', bare: false, panel: false },
);

const { t } = useTexts();

const labels = computed(() => availablePlatformLabels(props.flags));

const showCta = computed(() => appIsAvailable(props.flags));

/**
 * "pelo aplicativo", "no computador ou pelo aplicativo"… A junção é do texts.json para
 * que a redação inteira viva num lugar só.
 */
const where = computed(() => labels.value.join(t('platform_gate.where_join')));

const title = computed(() =>
  labels.value.length === 0
    ? t('platform_gate.title_nowhere', { unlocks: props.unlocks })
    : // UMA SÓ LIBERADA leva "somente", e a diferença não é enfeite: "está disponível
      // no aplicativo" deixa no ar se há outros lugares, e o jogador tenta o navegador
      // de novo. "Somente no aplicativo" fecha a porta e aponta a única aberta.
      t(
        labels.value.length === 1
          ? 'platform_gate.title_only'
          : 'platform_gate.title',
        { unlocks: props.unlocks, where: where.value },
      ),
);

const description = computed(() => {
  // AS TRÊS DESMARCADAS: não existe "lá" para apontar. O título já diz isso
  // (`title_nowhere`), e a descrição não pode mandar o jogador para um lugar que a
  // própria copy acabou de negar. O caso é alcançável por escrita direta no banco — o
  // backoffice recusa as três.
  if (labels.value.length === 0) {
    return t('platform_gate.description_nowhere');
  }

  return showCta.value
    ? t('platform_gate.description')
    : t('platform_gate.description_no_cta');
});

/**
 * O CTA ABRE A MODAL "Baixe o aplicativo" (ENG-495), a mesma que o menu lateral abre —
 * não manda direto para uma loja.
 *
 * Três coisas se resolvem de uma vez. O cartaz de lá mostra AS DUAS lojas, então o
 * aparelho deixa de ser adivinhado: era o buraco do iPad, que manda UA de Mac desde o
 * iPadOS 13 e caía na Play Store. A atribuição passa a ser a real: a campanha
 * `modal_app_generico` é justamente aquela modal, e apontar para os links dela sem
 * abri-la registrava instalações num cartaz que o jogador nunca viu. E o jogador ganha
 * o contexto do app (nota, avaliações, classificação) antes de sair do site.
 */
const appDownload = useAppDownloadStore();

// A marca do app, só no `panel` e só quando o app é a saída: no caso espelhado (a
// feature aberta no navegador e o jogador dentro do app) a arte estaria anunciando
// justamente o lugar de onde ele veio.
const appLogo = getAssetsUrlForCDN(
  '/assets/images/app-download/logo-horizontal-white.webp',
);
</script>

<template>
  <!-- PAINEL (a Loja): ocupa a folha no lugar do conteúdo, no idioma do
       `BaseLevelGateNotice` — a mesma vaga, o mesmo fundo `#010612`, o mesmo brilho
       radial saindo de cima e a mesma animação de entrada. Duas travas da mesma tela
       (nível e plataforma) não podem ter duas caras. -->
  <section
    v-if="panel"
    class="pag"
    data-testid="platform-app-only-gate"
    role="note"
  >
    <img
      v-if="showCta"
      alt=""
      aria-hidden="true"
      class="pag__logo"
      decoding="async"
      draggable="false"
      :src="appLogo"
    />

    <h2 class="pag__title">{{ title }}</h2>

    <p class="pag__text">{{ description }}</p>

    <button
      v-if="showCta"
      class="pag-cta pag-cta--panel"
      data-testid="platform-app-only-cta"
      type="button"
      @click="appDownload.open()"
    >
      <NuxtIcon
        aria-hidden="true"
        class="pag-cta__icon"
        name="download"
      />
      {{ t('platform_gate.cta') }}
    </button>

    <slot name="extra" />
  </section>

  <!-- `pointer-events-none` no bloco de texto e `auto` só no CTA: o véu por fora já
       barra o toque no que está atrás, e aqui isso evita que uma seleção acidental de
       texto atrapalhe o clique no botão. -->
  <div
    v-else
    class="pointer-events-none flex w-full max-w-[360px] flex-col items-center gap-2.5 text-center"
    :class="
      bare
        ? '[text-shadow:0_1px_6px_rgba(0,0,0,0.6)]'
        : 'rounded-2xl border border-white/10 bg-black/60 px-4 py-3.5 shadow-[0_8px_32px_rgba(0,0,0,0.45)] backdrop-blur-md'
    "
    data-testid="platform-app-only-gate"
  >
    <p class="text-base leading-tight font-bold text-white">
      {{ title }}
    </p>

    <p class="text-[13px] leading-snug text-white/90">
      {{ description }}
    </p>

    <button
      v-if="showCta"
      class="pag-cta pointer-events-auto"
      data-testid="platform-app-only-cta"
      type="button"
      @click="appDownload.open()"
    >
      <NuxtIcon
        aria-hidden="true"
        class="pag-cta__icon"
        name="download"
      />
      {{ t('platform_gate.cta') }}
    </button>

    <!-- Mesmo slot do `MinigameTurnoverGate`, e é o que deixa o check-in pendurar
         o "Ver os prêmios" aqui: quem monta a parede sabe o que mais cabe nela. -->
    <slot name="extra" />
  </div>
</template>

<style scoped lang="scss">
// Animação por @keyframes, nunca `transition`: o reset global do main.scss mata
// `transition` no app inteiro (mesma nota do `BaseLevelGateNotice`).
@keyframes pag-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

.pag {
  display: flex;
  flex-direction: column;
  align-items: center;
  // CENTRADO NA VERTICAL porque o painel é ALTO: ele toma a folha da Loja inteira em
  // vez de virar uma faixa no topo com o catálogo ausente por baixo. `dvh` e não `vh`
  // para a barra do navegador móvel não somar altura que não existe.
  justify-content: center;
  min-height: min(72dvh, 640px);
  gap: 10px;
  width: 100%;
  padding: 28px 20px 24px;
  text-align: center;
  // Aviso é só leitura: nada de seleção nem de arraste da arte.
  user-select: none;
  -webkit-user-select: none;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  // Fallback antes do gradiente: navegador que não conheça `color-mix` descarta a
  // declaração INTEIRA, e o painel ficaria transparente.
  background: #010612;
  // Dourado do CTA saindo de cima, sobre o fundo escuro — o brilho é a cor da SAÍDA
  // (baixar o app), como no aviso de nível ele é a cor do elo que falta.
  background:
    radial-gradient(
      120% 80% at 50% 0%,
      color-mix(in srgb, #ffc94a 16%, transparent) 0%,
      transparent 70%
    ),
    #010612;
  animation: pag-in 260ms ease-out both;
}

.pag__logo {
  width: 168px;
  height: auto;
  -webkit-user-drag: none;
}

.pag__title {
  max-width: 460px;
  margin: 0;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.pag__text {
  max-width: 420px;
  margin: 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
}

/* O BOTÃO, um só para as três apresentações. Dourado em gradiente com brilho por
   baixo, no mesmo idioma do CTA do aviso de nível (`BaseLevelGateNotice`) e dos cards
   de bônus app-only — o `#f0c04b` é o `--v2-gold` dos tokens, não um hex solto.

   `@keyframes` e não `transition` no hover: o reset global do main.scss mata
   `transition` no app inteiro, então o estado de toque é feito com `filter`, que
   aplica na hora. */
.pag-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  padding: 11px 24px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(92deg, #ff9d00 0%, #f0c04b 55%, #ffd982 100%);
  box-shadow:
    0 6px 18px rgba(240, 192, 75, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.45);
  color: #1a0f00;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.01em;
  white-space: nowrap;
  cursor: pointer;
  // O `bare` põe text-shadow no bloco todo para o texto ler sobre a roda; herdado
  // aqui, ele sujava o rótulo escuro sobre o dourado.
  text-shadow: none;

  &:hover {
    filter: brightness(1.06);
  }

  &:active {
    filter: brightness(0.96);
  }
}

.pag-cta__icon {
  display: inline-flex;
  font-size: 15px;
}

/* No painel da Loja o botão tem folga de sobra, então ele cresce junto com o resto. */
.pag-cta--panel {
  padding: 12px 28px;
  font-size: 14px;
}

@media (min-width: 768px) {
  .pag {
    // No desktop a folha da Loja é mais curta que a viewport (header + abas por
    // fora), então o teto em px evita um painel de 900px com o conteúdo perdido no
    // meio de um vazio.
    min-height: min(64dvh, 560px);
    padding: 40px 24px 36px;
    gap: 14px;
  }

  .pag__logo {
    width: 208px;
  }

  .pag__title {
    font-size: 22px;
  }

  .pag__text {
    font-size: 14px;
  }
}
</style>
